(() => {
  'use strict';

  const DPR_CAP = 1.5;
  const COVER_DWELL_MS = 150;
  const ALT_DWELL_MS = 1000;
  const COVER_TIMEOUT_MS = 30000;
  const hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const cards = new Set();
  const coverQueue = [];
  const altQueue = [];
  let activeCovers = 0;
  let activeAlternates = 0;
  let sequence = 0;
  let initialUpgradePhase = document.readyState === 'loading';
  let stillScanScheduled = false;

  const isHoverCapable = () => hoverQuery.matches;
  const previewParams = new URLSearchParams(location.search);
  const previewEnabled = previewParams.get('vc_paired_preview') === '1';
  const stationaryCropPreview = previewEnabled &&
    previewParams.get('vc_paired_renderer') === 'object';
  const debugTimePreview = previewEnabled && previewParams.get('vc_paired_debug_time') === '1';
  const hardCutPreview = previewEnabled && previewParams.get('vc_paired_hard_cut') === '1';
  const diagnosticNavPreview = previewEnabled && previewParams.get('vc_paired_diagnostic') === '1';
  const videoIds = new WeakMap();
  let videoIdSequence = 0;
  const videoId = (video) => {
    if (!video) return null;
    if (!videoIds.has(video)) videoIds.set(video, ++videoIdSequence);
    return videoIds.get(video);
  };
  const isConstrained = () => {
    if (connection && connection.saveData) return true;
    return ['slow-2g', '2g', '3g'].includes(connection && connection.effectiveType);
  };
  const coverConcurrency = () => (isHoverCapable() ? 4 : 3);
  const altConcurrency = () => (innerWidth >= 1280 ? 2 : 1);
  const initialStillsSettled = () => {
    if (document.readyState === 'loading') return false;
    for (const card of cards) {
      if (card.isConnected && card.initialInView && card.still && !card.still.complete) return false;
    }
    return true;
  };
  const scanNearbyStills = () => {
    stillScanScheduled = false;
    if (!initialStillsSettled()) return;
    for (const card of cards) {
      if (!card.isConnected || card.stillAssigned) continue;
      const rect = card.getBoundingClientRect();
      if (rect.top < innerHeight * 1.75 && rect.bottom > -innerHeight * 0.5) card.assignStill(card.currentRole);
    }
  };
  const scheduleStillScan = () => {
    if (stillScanScheduled) return;
    stillScanScheduled = true;
    requestAnimationFrame(scanNearbyStills);
  };

  const removeFromQueue = (queue, card) => {
    let index = queue.findIndex((item) => item.card === card);
    while (index !== -1) {
      queue.splice(index, 1);
      index = queue.findIndex((item) => item.card === card);
    }
  };

  const queueCover = (card, immediate = false) => {
    if (!card.isConnected || card.coverReadyForCurrentRole() || card.coverLoading) return;
    if (card.autoplayBlockedRoles.has(card.motionRole())) {
      card.coverState = 'static';
      return;
    }
    removeFromQueue(coverQueue, card);
    card.coverState = 'armed';
    const enqueue = () => {
      card.coverDwellTimer = 0;
      if (!card.isConnected || card.visibilityRatio < 0.25 || card.coverReadyForCurrentRole()) return;
      coverQueue.push({ card, priority: card.initialInView ? 0 : 1, top: card.getBoundingClientRect().top, sequence: sequence++ });
      coverQueue.sort((a, b) => a.priority - b.priority || a.top - b.top || a.sequence - b.sequence);
      pumpCoverQueue();
    };
    clearTimeout(card.coverDwellTimer);
    if (immediate) enqueue();
    else card.coverDwellTimer = setTimeout(enqueue, COVER_DWELL_MS);
  };

  const pumpCoverQueue = () => {
    if (coverQueue[0]?.priority === 0 && !initialStillsSettled()) return;
    while (activeCovers < coverConcurrency() && coverQueue.length) {
      const job = coverQueue.shift();
      const card = job.card;
      if (!card.isConnected || card.visibilityRatio < 0.25 || card.coverReadyForCurrentRole()) continue;
      activeCovers += 1;
      card.startCoverLoad(() => {
        activeCovers = Math.max(0, activeCovers - 1);
        pumpCoverQueue();
        pumpAltQueue();
      });
    }
  };

  const everyInViewCoverSettled = () => {
    for (const card of cards) {
      if (card.isConnected && card.visibilityRatio >= 0.25 && !card.coverSettledForCurrentRole()) return false;
    }
    return activeCovers === 0 && coverQueue.length === 0;
  };

  const queueAlternate = (card, promoted = false) => {
    if (!isHoverCapable() || !card.canPrepareAlternate() || card.altQueued || card.altLoading) return;
    if (!promoted && !card.initialInView) return;
    if (isConstrained() && !promoted) return;
    card.altQueued = true;
    altQueue.push({ card, promoted, top: card.getBoundingClientRect().top, sequence: sequence++ });
    altQueue.sort((a, b) => Number(b.promoted) - Number(a.promoted) || a.top - b.top || a.sequence - b.sequence);
    pumpAltQueue();
  };

  const pumpAltQueue = () => {
    if (!everyInViewCoverSettled()) return;
    while (activeAlternates < altConcurrency() && altQueue.length) {
      const job = altQueue.shift();
      const card = job.card;
      card.altQueued = false;
      if (!card.isConnected || !card.canPrepareAlternate()) continue;
      if (card.visibilityRatio <= 0 && !job.promoted) continue;
      activeAlternates += 1;
      card.startAlternateLoad(() => {
        activeAlternates = Math.max(0, activeAlternates - 1);
        pumpAltQueue();
      });
    }
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.onViewportChange(entry));
  }, { threshold: [0, 0.25] });

  const presentedFrame = (video, callback) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      callback();
    };
    if ('requestVideoFrameCallback' in video) {
      video.requestVideoFrameCallback(finish);
    } else {
      video.addEventListener('playing', () => requestAnimationFrame(() => requestAnimationFrame(finish)), { once: true });
    }
    return finish;
  };

  class VcCardMedia extends HTMLElement {
    constructor() {
      super();
      this.roles = { primary: null, alternate: null };
      this.records = { primary: null, alternate: null };
      this.autoplayBlockedRoles = new Set();
      this.currentRole = 'primary';
      this.visibilityRatio = 0;
      this.coverState = 'idle';
      this.coverLoading = false;
      this.coverDwellTimer = 0;
      this.altDwellTimer = 0;
      this.altQueued = false;
      this.altLoading = false;
      this.hovered = false;
      this.initialInView = false;
      this.hasEnteredViewport = false;
      this.onModeChange = (event) => this.setCoverRole(event.detail && event.detail.role, true);
      this.onPointerEnter = () => this.revealAlternate();
      this.onPointerLeave = () => this.hideAlternate();
      this.onFocusIn = () => this.revealAlternate();
      this.onFocusOut = (event) => {
        if (!event.relatedTarget || !this.closest('.card-wrapper')?.contains(event.relatedTarget)) this.hideAlternate();
      };
    }

    connectedCallback() {
      this.initialize();
      if (this.dataset.vcInitialized !== 'true') {
        requestAnimationFrame(() => {
          if (this.isConnected && this.dataset.vcInitialized !== 'true') this.initialize();
        });
      }
    }

    initialize() {
      if (this.dataset.vcInitialized === 'true') return;
      const json = this.querySelector('[data-vc-roles]');
      if (!json) return;
      this.dataset.vcInitialized = 'true';
      try { this.roles = JSON.parse(json ? json.textContent : '{}'); }
      catch (error) {
        this.coverState = 'static';
        this.dispatchEvent(new CustomEvent('vc:card-media:error', { bubbles: true, detail: { reason: 'invalid-data' } }));
        return;
      }
      Object.values(this.roles).forEach((role) => {
        if (!role || !role.source_media) return;
        role.sources = (role.source_media.sources || []).map((source) => ({
          width: source.width,
          height: source.height,
          url: source.url,
          type: source.mime_type || source.type
        }));
        delete role.source_media;
      });
      // Explicit card data is optional. Unmigrated products keep the V1/V2 path.
      let productionContract = this.querySelector('[data-vc-card-contract]');
      let contractNode = productionContract ||
        (previewEnabled && this.querySelector('[data-vc-card-preview-contract]'));
      let contract = null;
      try { contract = contractNode ? JSON.parse(contractNode.textContent) : null; }
      catch (_) { contract = null; }
      // Invalid production data cannot outrank an explicitly requested
      // diagnostic. Restore gallery stills before trying that lower tier.
      if (productionContract) {
        const candidateSources = Array.isArray(contract?.source_media?.sources) ? contract.source_media.sources : [];
        const playableSources = candidateSources.filter((source) =>
          source?.url && (!(source.mime_type || source.type) || (source.mime_type || source.type).includes('mp4')) &&
          Number(source.width) > 0 && Number(source.height) > 0);
        const ratio = contract?.card_type === 'dual' ? 1.5 : 0.75;
        const tolerance = contract?.card_type === 'dual' ? 0.03 : 0.02;
        const valid = ['dual', 'single'].includes(contract?.card_type) && playableSources.length > 0 &&
          playableSources.every((source) =>
            Math.abs(Number(source.width) / Number(source.height) - ratio) <= tolerance);
        if (!valid) {
          this.dataset.vcCardContractError = 'invalid-source-or-geometry';
          this.dispatchEvent(new CustomEvent('vc:card-media:error', {
            bubbles: true, detail: { reason: 'invalid-card-contract' }
          }));
          for (const role of ['primary', 'alternate']) {
            if (this.roles[role]?.fallback_preview?.src) this.roles[role].preview = this.roles[role].fallback_preview;
            else if (role === 'alternate') this.roles.alternate = null;
          }
          productionContract = null;
          contractNode = previewEnabled && this.querySelector('[data-vc-card-preview-contract]');
          try { contract = contractNode ? JSON.parse(contractNode.textContent) : null; }
          catch (_) { contract = null; }
        }
      }
      // Explicit stills can accompany future card-only assets; existing product
      // preview images remain the migration fallback when these are absent.
      if (contract?.stills?.imagery?.src) {
        this.roles.primary = { ...(this.roles.primary || {}), preview: contract.stills.imagery };
      }
      if (contract?.stills?.null?.src) {
        this.roles.alternate = { ...(this.roles.alternate || {}), preview: contract.stills.null };
      }
      if (contract?.card_type === 'single' && contract?.stills?.single?.src) {
        this.roles.primary = { ...(this.roles.primary || {}), preview: contract.stills.single };
      }
      const rawSources = Array.isArray(contract?.sources) ? contract.sources :
        (Array.isArray(contract?.source_media?.sources) ? contract.source_media.sources : []);
      const sources = rawSources.map((source) => ({
        url: source?.url, width: source?.width, height: source?.height,
        type: source?.type || source?.mime_type
      })).filter((source) => source.url && (!source.type || source.type.includes('mp4')) &&
        Number(source.width) > 0 && Number(source.height) > 0);
      const withinRatio = (source, target, tolerance) =>
        Math.abs(Number(source.width) / Number(source.height) - target) <= tolerance;
      const dual = contract?.card_type === 'dual' && sources.length && this.roles.primary?.preview?.src && this.roles.alternate?.preview?.src &&
        sources.every((source) => withinRatio(source, 1.5, 0.03));
      const single = contract?.card_type === 'single' && sources.length && this.roles.primary?.preview?.src &&
        sources.every((source) => withinRatio(source, 0.75, 0.02));
      // Touch retains current-cover-only loading for legacy V1/V2 cards. A
      // valid production dual is one stream and can select either half.
      const pairedMobilePreview = previewEnabled && !isHoverCapable() &&
        Boolean(this.querySelector('[data-vc-card-preview-contract]'));
      this.mediaModel = dual && (productionContract || isHoverCapable() || pairedMobilePreview) ? 'dual' : (single ? 'single' : 'legacy');
      this.cardSources = this.mediaModel === 'legacy' ? [] : sources;
      this.dataset.vcSwitchable = (this.mediaModel === 'dual' ||
        (this.mediaModel === 'legacy' && Boolean(this.roles.alternate))) ? 'true' : 'false';
      if (this.mediaModel !== 'legacy') this.dataset.vcCardType = this.mediaModel;
      if (productionContract && this.mediaModel !== 'legacy') this.dataset.vcProduction = 'true';
      if (this.mediaModel === 'dual' && (productionContract || stationaryCropPreview)) this.dataset.vcRenderer = 'object';
      if (this.mediaModel === 'dual' && (productionContract || hardCutPreview)) this.dataset.vcHardCutPreview = 'true';
      this.still = this.querySelector('[data-vc-still]');
      this.stillAssigned = false;
      this.assignedStillRole = null;
      this.scope = this.closest('[data-vc-cover-scope]');
      const globalRole = this.scope?.matches('[data-vc-global-media-scope]') &&
        window.VelveleCardPreviewMode?.role;
      const requested = globalRole ||
        (this.scope && this.scope.dataset.vcCover === 'alternate' ? 'alternate' : 'primary');
      this.currentRole = this.mediaModel === 'single' ? 'primary' : (this.roles[requested] ? requested : 'primary');
      this.applyCardPanel();
      const rect = this.getBoundingClientRect();
      this.initialInView = initialUpgradePhase && rect.bottom > 0 && rect.top < innerHeight;
      if (this.initialInView) this.assignStill(this.currentRole);
      this.cardRoot = this.closest('.card-wrapper') || this.parentElement;
      if (this.mediaModel === 'dual' && debugTimePreview) this.initializeDiagnostic();
      if (this.scope) this.scope.addEventListener('vc:cover-change', this.onModeChange);
      if (isHoverCapable() && this.cardRoot) {
        this.cardRoot.addEventListener('pointerenter', this.onPointerEnter);
        this.cardRoot.addEventListener('pointerleave', this.onPointerLeave);
        this.cardRoot.addEventListener('focusin', this.onFocusIn);
        this.cardRoot.addEventListener('focusout', this.onFocusOut);
      }
      cards.add(this);
      observer.observe(this);
      this.dispatchEvent(new CustomEvent('vc:card-media:capability', {
        bubbles: true,
        detail: { switchable: this.dataset.vcSwitchable === 'true', mediaModel: this.mediaModel }
      }));
    }

    disconnectedCallback() {
      if (this.diagnostic) this.diagnostic.disconnected = true;
      clearTimeout(this.coverDwellTimer);
      clearTimeout(this.altDwellTimer);
      removeFromQueue(coverQueue, this);
      removeFromQueue(altQueue, this);
      observer.unobserve(this);
      cards.delete(this);
      if (this.scope) this.scope.removeEventListener('vc:cover-change', this.onModeChange);
      if (this.cardRoot) {
        this.cardRoot.removeEventListener('pointerenter', this.onPointerEnter);
        this.cardRoot.removeEventListener('pointerleave', this.onPointerLeave);
        this.cardRoot.removeEventListener('focusin', this.onFocusIn);
        this.cardRoot.removeEventListener('focusout', this.onFocusOut);
      }
      this.releaseAll();
    }

    assignStill(role) {
      const preview = this.roles[role] && this.roles[role].preview;
      if (!preview || !this.still) return;
      const visiblePriority = this.initialInView || this.visibilityRatio >= 0.25;
      this.still.loading = visiblePriority ? 'eager' : 'lazy';
      this.still.fetchPriority = this.initialInView && this.dataset.vcInitialPriority === 'high'
        ? 'high'
        : (visiblePriority ? 'auto' : 'low');
      if (preview.width) this.still.width = preview.width;
      if (preview.height) this.still.height = preview.height;
      this.still.dataset.vcAuto = preview.auto ? 'true' : 'false';
      const tileWidth = this.getBoundingClientRect().width;
      const displayDpr = devicePixelRatio || 1;
      const stillDpr = Math.min(displayDpr, 2);
      const stillWidthFactor = this.mediaModel === 'dual' && this.querySelector('[data-vc-card-contract]') ? 2 : 1;
      if (tileWidth > 0) this.still.sizes = `${Math.ceil(tileWidth * stillDpr * stillWidthFactor / displayDpr)}px`;
      else if (preview.sizes) this.still.sizes = preview.sizes;
      if (preview.srcset) this.still.srcset = preview.srcset;
      if (preview.src) this.still.src = preview.src;
      this.stillAssigned = true;
      this.assignedStillRole = role;
      if (this.initialInView && !this.still.complete && !this.still.dataset.vcPumpBound) {
        this.still.dataset.vcPumpBound = 'true';
        const resumeInitialCovers = () => pumpCoverQueue();
        this.still.addEventListener('load', resumeInitialCovers, { once: true });
        this.still.addEventListener('error', resumeInitialCovers, { once: true });
      }
    }

    motionRole() {
      return this.mediaModel === 'legacy' ? this.currentRole : 'primary';
    }

    applyCardPanel() {
      if (this.mediaModel === 'dual') {
        this.dataset.vcPanel = this.hovered ? this.alternateRole() : this.currentRole;
      }
    }

    initializeDiagnostic() {
      const handle = this.cardRoot?.dataset.product || 'unknown';
      this.diagnostic = { handle, events: [], toggles: [], frames: [], latestFrame: null, disconnected: false };
      const registry = window.__vcPairedDiagnostics ||= { cards: [] };
      registry.cards.push({ handle, card: this, log: this.diagnostic });
    }

    diagnosticSnapshot(label) {
      const video = this.records.primary?.element || null;
      const videoStyle = video && getComputedStyle(video);
      const stillStyle = this.still && getComputedStyle(this.still);
      const cardStyle = this.cardRoot && getComputedStyle(this.cardRoot);
      return { label, at: performance.now(), currentTime: video?.currentTime ?? null,
        mediaTime: this.diagnostic?.latestFrame?.mediaTime ?? null,
        videoId: videoId(video), src: video?.currentSrc || video?.src || null,
        paused: video?.paused ?? null, readyState: video?.readyState ?? null,
        panel: this.dataset.vcPanel, role: this.currentRole,
        videoOpacity: videoStyle?.opacity ?? null, videoVisibility: videoStyle?.visibility ?? null,
        stillOpacity: stillStyle?.opacity ?? null, stillDisplay: stillStyle?.display ?? null,
        cardOpacity: cardStyle?.opacity ?? null };
    }

    recordDiagnosticToggle(next) {
      if (!this.diagnostic) return null;
      const entry = { from: this.currentRole, to: next, samples: [this.diagnosticSnapshot('before')] };
      this.diagnostic.toggles.push(entry);
      return entry;
    }

    finishDiagnosticToggle(entry) {
      if (!entry) return;
      entry.samples.push(this.diagnosticSnapshot('immediate'));
      requestAnimationFrame(() => entry.samples.push(this.diagnosticSnapshot('raf')));
      [50, 100, 250].forEach((delay) => setTimeout(() => {
        entry.samples.push(this.diagnosticSnapshot(`+${delay}ms`));
      }, delay));
    }

    setCoverRole(role, userInitiated = false) {
      if (this.mediaModel === 'single') return;
      const next = role === 'alternate' && this.roles.alternate ? 'alternate' : 'primary';
      if (next === this.currentRole) return;
      if (this.mediaModel === 'dual') {
        const diagnosticToggle = this.recordDiagnosticToggle(next);
        this.currentRole = next;
        if (!this.coverReadyForCurrentRole() && this.visibilityRatio > 0) this.assignStill(next);
        this.applyCardPanel();
        this.finishDiagnosticToggle(diagnosticToggle);
        return;
      }
      const oldRole = this.currentRole;
      this.currentRole = next;
      clearTimeout(this.altDwellTimer);
      removeFromQueue(altQueue, this);
      this.altQueued = false;
      this.hovered = false;
      const oldRecord = this.records[oldRole];
      if (oldRecord?.loading && !oldRecord.ready) this.releaseRole(oldRole);
      this.coverState = this.autoplayBlockedRoles.has(this.motionRole()) ? 'static' :
        (this.coverReadyForCurrentRole() ? 'ready' : 'idle');
      this.classList.remove('is-cover-ready', 'is-alt-visible');
      if (this.visibilityRatio > 0) this.assignStill(next);
      this.syncRecordClasses();

      if (!isHoverCapable()) this.releaseRole(oldRole);
      else {
        const retainedRecord = this.records[oldRole];
        if (retainedRecord && retainedRecord.element) retainedRecord.element.pause();
      }

      if (this.coverReadyForCurrentRole()) {
        this.activateReadyCover();
      } else if (this.visibilityRatio >= 0.25 && !reducedMotionQuery.matches) {
        queueCover(this, userInitiated || this.initialInView);
      }
    }

    onViewportChange(entry) {
      this.visibilityRatio = entry.intersectionRatio;
      if (entry.isIntersecting && (!this.stillAssigned || this.assignedStillRole !== this.currentRole)) {
        this.assignStill(this.currentRole);
      }
      if (this.visibilityRatio >= 0.25) {
        const firstEntry = !this.hasEnteredViewport;
        this.hasEnteredViewport = true;
        if (!reducedMotionQuery.matches && !this.coverReadyForCurrentRole()) {
          queueCover(this, firstEntry && this.initialInView);
        } else if (this.coverReadyForCurrentRole()) {
          this.activateReadyCover();
        }
      } else {
        clearTimeout(this.coverDwellTimer);
        removeFromQueue(coverQueue, this);
        if (this.coverLoading && !this.coverReadyForCurrentRole()) this.releaseRole(this.motionRole());
        else this.pauseRole(this.motionRole());
      }

      if (this.visibilityRatio <= 0) {
        this.hideAlternate();
        if (this.mediaModel === 'legacy') {
          const alternate = this.alternateRole();
          const record = this.records[alternate];
          if (record && record.loading && !record.ready) this.releaseRole(alternate);
        }
      }
    }

    pickSource(role, forcedIndex) {
      const sources = (this.mediaModel === 'legacy' ?
        ((this.roles[role] && this.roles[role].sources) || []) : this.cardSources)
        .filter((source) => source.url && (!source.type || source.type.includes('mp4')))
        .sort((a, b) => Number(a.width || a.height) - Number(b.width || b.height));
      if (!sources.length) return { source: null, index: -1, sources };
      if (Number.isInteger(forcedIndex)) {
        const index = Math.min(sources.length - 1, Math.max(0, forcedIndex));
        return { source: sources[index], index, sources };
      }
      if (isConstrained()) return { source: sources[0], index: 0, sources };
      const panelMultiplier = this.mediaModel === 'dual' ? 2 : 1;
      const target = this.getBoundingClientRect().width * Math.min(devicePixelRatio || 1, DPR_CAP) * panelMultiplier;
      let index = sources.findIndex((source) => Number(source.width || 0) >= target * 0.9);
      if (index < 0) index = sources.length - 1;
      return { source: sources[index], index, sources };
    }

    createVideo(role, source) {
      const video = document.createElement('video');
      video.className = 'vc-motion';
      if (this.mediaModel === 'dual') video.classList.add('vc-paired-motion');
      video.dataset.vcRole = role;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      video.src = source.url;
      this.appendChild(video);
      if (this.diagnostic) {
        const names = ['seeking', 'seeked', 'loadstart', 'emptied', 'pause', 'play', 'playing', 'waiting', 'timeupdate'];
        names.forEach((name) => video.addEventListener(name, () => {
          this.diagnostic.events.push({ name, at: performance.now(), currentTime: video.currentTime,
            videoId: videoId(video), src: video.currentSrc || video.src, paused: video.paused,
            readyState: video.readyState });
          if (this.diagnostic.events.length > 600) this.diagnostic.events.shift();
        }));
        if (video.requestVideoFrameCallback) {
          const onFrame = (now, metadata) => {
            if (!video.isConnected || this.diagnostic.disconnected) return;
            const frame = { at: now, mediaTime: metadata.mediaTime, videoId: videoId(video) };
            this.diagnostic.latestFrame = frame;
            this.diagnostic.frames.push(frame);
            if (this.diagnostic.frames.length > 600) this.diagnostic.frames.shift();
            video.requestVideoFrameCallback(onFrame);
          };
          video.requestVideoFrameCallback(onFrame);
        }
      }
      return video;
    }

    markAutoplayBlocked(role, error) {
      this.autoplayBlockedRoles.add(role);
      const record = this.records[role];
      if (record?.element) {
        record.element.pause();
        record.element.classList.remove('is-presented');
      }
      if (role === this.motionRole()) {
        this.coverState = 'static';
        this.classList.remove('is-cover-ready');
        delete this.dataset.vcHardCutReady;
        if (!this.stillAssigned || this.assignedStillRole !== this.currentRole) this.assignStill(this.currentRole);
      }
      this.dispatchEvent(new CustomEvent('vc:card-media:error', {
        bubbles: true, detail: { role, reason: 'autoplay-denied', name: error?.name || 'NotAllowedError' }
      }));
    }

    startCoverLoad(done) {
      const role = this.motionRole();
      if (!this.roles[role] || this.coverReadyForCurrentRole()) { done(); return; }
      this.coverLoading = true;
      this.coverState = 'loading';
      this.loadRole(role, false, null, (success) => {
        this.coverLoading = false;
        if (role === this.motionRole()) {
          this.coverState = success ? 'ready' : 'static';
          if (success) this.activateReadyCover();
        }
        done();
      });
    }

    startAlternateLoad(done) {
      const role = this.alternateRole();
      this.altLoading = true;
      this.loadRole(role, true, null, (success) => {
        this.altLoading = false;
        if (success && this.records[role] && this.records[role].element) {
          this.records[role].element.pause();
          if (this.hovered) this.showPreparedAlternate();
        }
        done();
      });
    }

    loadRole(role, preparing, forcedIndex, done) {
      if (!this.roles[role] || !this.isConnected) { done(false); return; }
      const existing = this.records[role];
      if (existing && existing.ready) { done(true); return; }
      this.releaseRole(role);
      const picked = this.pickSource(role, forcedIndex);
      if (!picked.source) { done(false); return; }

      const video = this.createVideo(role, picked.source);
      const record = { element: video, ready: false, loading: true, sourceIndex: picked.index, preparing };
      this.records[role] = record;
      this.syncRecordClasses();
      let finished = false;
      const timeout = setTimeout(() => fail('timeout'), COVER_TIMEOUT_MS);
      const finish = (success) => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        record.loading = false;
        if (success) {
          record.ready = true;
          video.classList.add('is-presented');
          this.dispatchEvent(new CustomEvent('vc:card-media:ready', { bubbles: true, detail: { role, width: picked.source.width } }));
        }
        done(success);
      };
      record.cancel = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        record.loading = false;
        done(false);
      };
      const fail = (reason, error) => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        if (error?.name === 'NotAllowedError') {
          this.markAutoplayBlocked(role, error);
          this.releaseRole(role, false);
          done(false);
          return;
        }
        const nextIndex = picked.index - 1;
        this.releaseRole(role, false);
        if (nextIndex >= 0) {
          this.loadRole(role, preparing, nextIndex, done);
        } else {
          this.dispatchEvent(new CustomEvent('vc:card-media:error', { bubbles: true, detail: { role, reason } }));
          done(false);
        }
      };
      video.addEventListener('error', () => fail('media-error'), { once: true });
      presentedFrame(video, () => finish(true));
      video.load();
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch((error) => fail('play-rejected', error));
    }

    syncRecordClasses() {
      if (this.mediaModel !== 'legacy') {
        const video = this.records.primary?.element;
        if (video) {
          video.classList.add('is-cover');
          video.classList.remove('is-hover');
        }
        this.applyCardPanel();
        return;
      }
      Object.entries(this.records).forEach(([role, record]) => {
        if (!record || !record.element) return;
        const cover = role === this.currentRole;
        record.element.classList.toggle('is-cover', cover);
        record.element.classList.toggle('is-hover', !cover);
      });
    }

    activateReadyCover() {
      const role = this.motionRole();
      const record = this.records[role];
      if (!record || !record.ready || this.autoplayBlockedRoles.has(role)) return;
      this.syncRecordClasses();
      this.classList.add('is-cover-ready');
      if (this.dataset.vcHardCutPreview === 'true' && this.mediaModel === 'dual') this.dataset.vcHardCutReady = 'true';
      if (this.visibilityRatio >= 0.25 && !document.hidden) {
        const promise = record.element.play();
        if (promise && typeof promise.catch === 'function') promise.catch((error) => {
          if (error?.name === 'NotAllowedError' && this.records[role] === record) {
            this.markAutoplayBlocked(role, error);
          }
        });
      }
      clearTimeout(this.altDwellTimer);
      if (this.mediaModel === 'legacy' && this.initialInView && isHoverCapable() && this.roles[this.alternateRole()] && !isConstrained()) {
        this.altDwellTimer = setTimeout(() => queueAlternate(this, false), ALT_DWELL_MS);
      }
    }

    coverReadyForCurrentRole() {
      const record = this.records[this.motionRole()];
      return Boolean(record && record.ready && !this.autoplayBlockedRoles.has(this.motionRole()));
    }

    coverSettledForCurrentRole() {
      return this.coverReadyForCurrentRole() || this.coverState === 'static';
    }

    alternateRole() {
      return this.currentRole === 'primary' ? 'alternate' : 'primary';
    }

    canPrepareAlternate() {
      if (this.mediaModel !== 'legacy') return false;
      const role = this.alternateRole();
      const record = this.records[role];
      return Boolean(this.roles[role] && !this.autoplayBlockedRoles.has(role) &&
        this.coverReadyForCurrentRole() && !(record && record.ready));
    }

    revealAlternate() {
      if (this.mediaModel === 'dual') {
        this.hovered = true;
        this.applyCardPanel();
        return;
      }
      if (this.mediaModel === 'single') return;
      if (!isHoverCapable() || !this.roles[this.alternateRole()]) return;
      this.hovered = true;
      const record = this.records[this.alternateRole()];
      if (record && record.ready) this.showPreparedAlternate();
      else queueAlternate(this, true);
    }

    showPreparedAlternate() {
      if (!this.hovered) return;
      const alternate = this.records[this.alternateRole()];
      const cover = this.records[this.currentRole];
      if (!alternate || !alternate.ready) return;
      if (cover && cover.element) cover.element.pause();
      this.classList.add('is-alt-visible');
      const promise = alternate.element.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    }

    hideAlternate() {
      if (this.mediaModel === 'dual') {
        this.hovered = false;
        this.applyCardPanel();
        return;
      }
      if (this.mediaModel === 'single') return;
      this.hovered = false;
      this.classList.remove('is-alt-visible');
      const alternate = this.records[this.alternateRole()];
      if (alternate && alternate.element) alternate.element.pause();
      if (this.visibilityRatio >= 0.25) this.activateReadyCover();
    }

    pauseRole(role) {
      const record = this.records[role];
      if (record && record.element) record.element.pause();
    }

    releaseRole(role, cancel = true) {
      const record = this.records[role];
      if (!record || !record.element) { this.records[role] = null; return; }
      if (cancel && record.cancel) record.cancel();
      const video = record.element;
      try { video.pause(); } catch (_) {}
      video.removeAttribute('src');
      try { video.load(); } catch (_) {}
      video.remove();
      this.records[role] = null;
      if (role === this.motionRole()) {
        this.classList.remove('is-cover-ready');
        delete this.dataset.vcHardCutReady;
        if (!record.ready) this.coverState = 'idle';
      }
    }

    releaseAll() {
      this.releaseRole('primary');
      this.releaseRole('alternate');
    }

    reconcile() {
      if (document.hidden || reducedMotionQuery.matches) {
        this.pauseRole('primary');
        this.pauseRole('alternate');
        return;
      }
      if (this.visibilityRatio >= 0.25) {
        if (this.coverReadyForCurrentRole()) this.activateReadyCover();
        else if (!reducedMotionQuery.matches) queueCover(this, true);
      }
    }
  }

  customElements.define('vc-card-media', VcCardMedia);
  if (debugTimePreview || diagnosticNavPreview) {
    document.addEventListener('DOMContentLoaded', () => {
      let diagnosticTargetHandle = null;
      const targets = [
        ['slideshow-ticker', 'Ticker'], ['slideshow-deck', 'Deck'],
        ['ripple-origin', 'Ripple'], ['bouncing-frame-origin', 'Bouncing']
      ];
      if (diagnosticNavPreview) {
        const nav = document.createElement('nav');
        nav.className = 'vc-paired-diagnostic-nav';
        nav.setAttribute('aria-label', 'Paired diagnostic cards');
        targets.forEach(([handle, label]) => {
          if (!document.querySelector(`.card-wrapper[data-product="${handle}"] vc-card-media`)) return;
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = label;
          button.addEventListener('click', () => {
            diagnosticTargetHandle = handle;
            document.querySelector(`.card-wrapper[data-product="${handle}"] vc-card-media`)
              ?.scrollIntoView({ block: 'center', behavior: 'auto' });
          });
          nav.appendChild(button);
        });
        document.body.appendChild(nav);
      }
      if (debugTimePreview) {
        const hud = document.createElement('output');
        hud.className = 'vc-paired-diagnostic-clock';
        hud.setAttribute('aria-live', 'off');
        document.body.appendChild(hud);
        const draw = () => {
          const cards = window.__vcPairedDiagnostics?.cards || [];
          const target = diagnosticTargetHandle && cards.find(({ handle }) => handle === diagnosticTargetHandle);
          if (target && !target.card.records.primary?.element) {
            hud.textContent = `${target.handle}  waiting for first video frame`;
            return;
          }
          if (target && target.card.visibilityRatio < 0.25) {
            hud.textContent = `${target.handle}  outside viewport`;
            return;
          }
          const visibleCards = cards
            .filter(({ card }) => card.isConnected && card.visibilityRatio >= 0.25 && card.records.primary?.element)
            .sort((a, b) => b.card.visibilityRatio - a.card.visibilityRatio);
          const inView = target || visibleCards[0];
          if (!inView) { hud.textContent = 'Paired clock: no active card'; return; }
          const { card, handle, log } = inView;
          const snap = card.diagnosticSnapshot('hud');
          const last = log.toggles.at(-1);
          const before = last?.samples?.[0]?.currentTime;
          const latest = last?.samples?.at(-1)?.currentTime;
          hud.textContent = `${handle}  time ${snap.currentTime?.toFixed(2) ?? '—'}  frame ${snap.mediaTime?.toFixed(2) ?? '—'}\n` +
            `video #${snap.videoId ?? '—'}  last switch ${before?.toFixed(2) ?? '—'} → ${latest?.toFixed(2) ?? '—'}`;
        };
        draw();
        const timer = setInterval(draw, 100);
        window.addEventListener('pagehide', () => clearInterval(timer), { once: true });
      }
    }, { once: true });
  }
  document.addEventListener('DOMContentLoaded', () => {
    initialUpgradePhase = false;
    pumpCoverQueue();
  }, { once: true });

  document.addEventListener('visibilitychange', () => cards.forEach((card) => card.reconcile()));
  window.addEventListener('pageshow', () => cards.forEach((card) => card.reconcile()));
  window.addEventListener('scroll', scheduleStillScan, { passive: true });
  window.addEventListener('resize', scheduleStillScan, { passive: true });
  window.addEventListener('pagehide', () => cards.forEach((card) => {
    card.pauseRole('primary');
    card.pauseRole('alternate');
  }));
  reducedMotionQuery.addEventListener?.('change', () => cards.forEach((card) => card.reconcile()));
})();
